import {
  ArrowRight,
  Check,
  CreditCard,
  Loader2,
} from "lucide-react";
import { fmtMoney } from "@/lib/assessment";
import GstNote from "./GstNote";
import PromoCodeInput from "./PromoCodeInput";

const MEMBERSHIP_FEATURES = [
  "Full personalised Career Report (3 cross-checked AI pathways)",
  "Ongoing job matching across 5 sources with match scores",
  "Save jobs, track applications, and update next actions",
  "Government support screening with official sources",
  "Weekly 7 / 30 / 90 day action plan",
  "Cancel anytime after the 3-month minimum term",
];

export default function MembershipCard({
  sub,
  busy,
  onCheckout,
  promo,
  onPromoApply,
  onPromoClear,
}) {
  const pricing = sub.pricing;
  const active = sub.status === "active";
  return (
    <section
      className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col"
      data-testid="subscribe-pricing"
    >
      <header className="flex items-center gap-3 mb-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
          <CreditCard className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-lg font-medium text-slate-900">
            Career Transition Membership
          </h2>
          <p className="text-xs text-slate-500">
            Full programme · monthly billing
          </p>
        </div>
      </header>

      <div className="flex items-baseline gap-2 mt-2">
        <span
          className="text-5xl font-medium text-slate-900"
          data-testid="subscribe-price"
        >
          {fmtMoney(pricing.base_aud, "AUD")}
        </span>
        <span className="text-slate-500">/ month + GST</span>
      </div>
      <GstNote pricing={pricing} suffix=" / month" />

      <p
        className="mt-3 text-sm text-slate-600"
        data-testid="subscribe-min-term"
      >
        Minimum {sub.min_months}-month commitment ·{" "}
        {fmtMoney(sub.min_total_aud, "AUD")} + GST ={" "}
        <span className="font-medium text-slate-700">
          {fmtMoney(sub.min_total_inc_gst_aud, "AUD")} incl. GST
        </span>
      </p>

      <ul className="mt-6 space-y-2.5 flex-1" data-testid="subscribe-features">
        {MEMBERSHIP_FEATURES.map((f) => (
          <li
            key={f}
            className="flex items-start gap-2.5 text-sm text-slate-700"
          >
            <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
              <Check className="h-3 w-3" />
            </span>
            {f}
          </li>
        ))}
      </ul>

      <div
        className="mt-6 rounded-xl bg-slate-50 border border-slate-100 p-4 text-sm text-slate-600"
        data-testid="subscribe-cancellation-rules"
      >
        <p className="font-medium text-slate-800 mb-1">Cancellation</p>
        <p>
          Cancel anytime after your 3rd monthly payment. Before then, your
          membership runs to the end of the minimum {sub.min_months}-month term.
        </p>
      </div>

      <button
        type="button"
        onClick={() => onCheckout("membership")}
        disabled={busy?.plan === "membership"}
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-medium text-white shadow-md hover:bg-emerald-700 disabled:opacity-60"
        data-testid="subscribe-pay-button"
      >
        {busy?.plan === "membership" ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <CreditCard className="h-4 w-4" />
        )}
        {promo && promo.requires_payment === false
          ? `Activate free — ${promo.duration_months} ${
              promo.duration_months === 1 ? "month" : "months"
            }`
          : active
            ? `Pay for next month (${fmtMoney(
                promo?.total_aud ?? pricing.total_aud,
                "AUD",
              )} incl. GST)`
            : `Start membership — ${fmtMoney(
                promo?.total_aud ?? pricing.total_aud,
                "AUD",
              )}/mo incl. GST`}
        <ArrowRight className="h-4 w-4" />
      </button>
      <PromoCodeInput
        plan="membership"
        applied={promo}
        onApply={onPromoApply}
        onClear={onPromoClear}
      />
      <p className="mt-2 text-xs text-slate-500">
        Secured by Stripe. AUD billing.
      </p>
    </section>
  );
}
